// 찜질방 씻는 자리의 바가지: 손잡이 없는 동그란 파란 한국식 플라스틱 바가지로 대야에서 물을 떠,
// 팔을 바깥쪽 위로 사선으로 들고 머리 쪽으로 홱 기울여 물을 끼얹는다 (물이 바가지에서 튀어나와 머리에 쏟아진다).
// 동작 박자는 pose.ts washCycle과 같다. 프로토타입 washProps 3539–3550을 4단계 피드백으로 다시 만든 것.
import * as THREE from 'three';
import { lathe } from '../../engine/geometry';
import { steamTex } from '../../engine/textures';
import { washCycle } from '../../capy/pose';
import type { Capy } from '../../capy/agent';

const BLUE = new THREE.MeshStandardMaterial({ color: 0x4F9FE3, roughness: 0.35, side: THREE.DoubleSide });
const WATER = new THREE.MeshStandardMaterial({ color: 0xBFE6FF, roughness: 0.05, transparent: true, opacity: 0.8, emissive: 0x9FD8FF, emissiveIntensity: 0.3 });
const DROP_MAT = new THREE.MeshStandardMaterial({ color: 0xDDF2FF, roughness: 0.05, transparent: true, opacity: 0.7, emissive: 0xAEE0FF, emissiveIntensity: 0.4, depthWrite: false });
// 바가지: 둥글고 깊은 그릇 (반지름 0.16, 깊이 0.11, BOWL_K배) + 안쪽 + 둥근 입술, 모두 파란색
const BOWL_GEO = lathe([[0, -0.11], [0.09, -0.104], [0.145, -0.065], [0.16, -0.01], [0.152, -0.006], [0.138, -0.06], [0.086, -0.096], [0, -0.1]], 26);
const RIM_GEO = new THREE.TorusGeometry(0.156, 0.011, 6, 30);
const DROP_GEO = new THREE.SphereGeometry(0.02, 8, 6);
/** 바가지 크기 배율 (2차 피드백: 더 크게). */
const BOWL_K = 1.4, BOWL_R = 0.16 * BOWL_K;

interface Drop { m: THREE.Mesh; v: THREE.Vector3; alive: boolean }
interface Splash { s: THREE.Sprite; t0: number; big: number }
interface Dipper { g: THREE.Group; bowl: THREE.Group; fill: THREE.Mesh; drops: Drop[]; splashes: Splash[]; emit: number; gushed: boolean; world: THREE.Object3D }

const all = new WeakMap<Capy, Dipper>();
const Y = new THREE.Vector3(0, 1, 0), paw = new THREE.Vector3(), sh = new THREE.Vector3(), head = new THREE.Vector3(), out = new THREE.Vector3();
const h = new THREE.Vector3(), lip = new THREE.Vector3(), axis = new THREE.Vector3(), tmp = new THREE.Vector3(), qa = new THREE.Quaternion(), qb = new THREE.Quaternion();

function make(world: THREE.Object3D): Dipper {
  const g = new THREE.Group(), bowl = new THREE.Group();
  bowl.add(new THREE.Mesh(BOWL_GEO, BLUE));
  const rim = new THREE.Mesh(RIM_GEO, BLUE); rim.rotation.x = Math.PI / 2; rim.position.y = -0.006; bowl.add(rim);
  const fill = new THREE.Mesh(new THREE.CircleGeometry(0.135, 22), WATER); fill.rotation.x = -Math.PI / 2; fill.position.y = -0.03; bowl.add(fill);
  g.add(bowl);
  world.add(g);
  const drops: Drop[] = [];
  for (let i = 0; i < 70; i++) { const m = new THREE.Mesh(DROP_GEO, DROP_MAT); m.visible = false; g.add(m); drops.push({ m, v: new THREE.Vector3(), alive: false }); }
  const splashes: Splash[] = [];
  for (let i = 0; i < 14; i++) { const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: steamTex(), transparent: true, depthWrite: false, opacity: 0 })); s.visible = false; g.add(s); splashes.push({ s, t0: -9, big: 1 }); }
  return { g, bowl, fill, drops, splashes, emit: 0, gushed: false, world };
}

function splashAt(D: Dipper, p: THREE.Vector3, t: number, big = 1) {
  const sp = D.splashes.reduce((o, s) => (s.t0 < o.t0 ? s : o));
  sp.t0 = t; sp.big = big; sp.s.position.copy(p); sp.s.visible = true;
}

/** 매 프레임. on: 지금 씻는 자리에서 바가지로 씻는 중이고 보이는지. */
export function dipperTick(a: Capy, t: number, dt: number, on: boolean, world: THREE.Object3D): void {
  let D = all.get(a);
  if (!on) {
    if (D) { D.bowl.visible = false; D.drops.forEach((d) => { d.alive = false; d.m.visible = false; }); D.splashes.forEach((s) => { s.s.visible = false; }); }
    return;
  }
  if (!D || D.world !== world) { if (D) D.g.parent?.remove(D.g); all.set(a, D = make(world)); }
  const q = ((t - (a.washT || 0)) / 3.2 * 2) % 1, w = washCycle(q), c = a.c, sz = a.size || 1;
  c.root.updateMatrixWorld(true);
  c.paws[0]!.getWorldPosition(paw);
  c.arms[0]!.getWorldPosition(sh);
  c.headG.localToWorld(head.set(0, 0.6, 0.1));
  // 바가지는 앞발 바로 바깥쪽 (어깨 → 앞발 방향으로 조금 더)
  out.copy(paw).sub(sh); out.y = Math.max(out.y, 0.05); out.normalize();
  const bowlC = tmp.copy(paw).addScaledVector(out, 0.2 * sz);
  D.bowl.visible = true;
  D.bowl.position.copy(bowlC); D.bowl.scale.setScalar(sz * BOWL_K);
  // 끼얹을 때는 입구를 머리 쪽으로 홱 기울인다
  h.set(head.x - bowlC.x, 0, head.z - bowlC.z); if (h.lengthSq() < 1e-4) h.set(-Math.sin(a.facing), 0, -Math.cos(a.facing)); h.normalize();
  qa.setFromAxisAngle(Y, Math.atan2(-h.z, h.x) + Math.PI);
  axis.crossVectors(Y, h).normalize();
  qb.setFromAxisAngle(axis, 1.9 * w.pour - 0.25 * w.dip);
  D.bowl.quaternion.copy(qb).multiply(qa);
  // 바가지 속 물: 뜰 때 차고, 끼얹으면 비고, 내리면 다시 뜬다
  const full = q < 0.52 ? (q < 0.03 ? 0.3 : 1) : q < 0.74 ? 1 - (q - 0.52) / 0.22 : 0;
  D.fill.visible = full > 0.05; D.fill.scale.setScalar(Math.max(0.05, full));
  // 입구 쪽 가장자리에서 물이 튀어나와 포물선으로 머리에 쏟아진다
  lip.copy(bowlC).addScaledVector(h, BOWL_R * sz); lip.y += 0.04 * sz;
  const throwing = w.pour > 0.3 && q >= 0.52 && q < 0.74;
  if (throwing) {
    if (!D.gushed) { D.gushed = true; splashAt(D, lip, t, 1.6); }
    D.emit += dt * 130;
    const dx = head.x - lip.x, dz = head.z - lip.z, dh = Math.hypot(dx, dz) || 1, rise = Math.max(0.2, head.y + 0.15 - lip.y);
    // 머리 꼭대기 위를 지나 떨어지도록: 위로 vy, 옆으로 dh/T
    const vy = Math.sqrt(2 * 6 * (rise + 0.15)), T = (vy + Math.sqrt(Math.max(0, vy * vy - 2 * 6 * (head.y - 0.05 - lip.y)))) / 6, vh = dh / Math.max(0.2, T);
    while (D.emit >= 1) {
      D.emit -= 1;
      const d = D.drops.find((x) => !x.alive); if (!d) break;
      d.alive = true; d.m.visible = true; d.m.position.copy(lip).add(tmp.set((Math.random() - 0.5) * 0.12, 0, (Math.random() - 0.5) * 0.12).multiplyScalar(sz));
      const j = 0.75 + Math.random() * 0.5;
      d.v.set(dx / dh * vh * j + (Math.random() - 0.5) * 0.5, vy * (0.85 + Math.random() * 0.3), dz / dh * vh * j + (Math.random() - 0.5) * 0.5);
    }
  } else { D.emit = 0; if (w.pour < 0.1) D.gushed = false; }
  // 물방울: 머리에 닿으면 튀며 미끄러져 내리고, 바닥에 닿으면 사라진다
  for (const d of D.drops) {
    if (!d.alive) continue;
    d.v.y -= 6 * dt; d.m.position.addScaledVector(d.v, dt);
    const sp = d.v.length(); d.m.scale.set(1, 1 + Math.min(2.2, sp * 0.6), 1); if (sp > 1e-3) d.m.quaternion.setFromUnitVectors(Y, tmp.copy(d.v).divideScalar(sp));
    const p = d.m.position;
    if (d.v.y < 0 && Math.hypot(p.x - head.x, p.z - head.z) < 0.6 * sz && p.y < head.y && p.y > head.y - 0.3 * sz) {
      if (Math.random() < 0.25) splashAt(D, p, t, 0.8);
      d.v.set((p.x - head.x) * 2.2, 0.3, (p.z - head.z) * 2.2); p.y = head.y + 0.01; continue;
    }
    if (p.y < 0.03) { d.alive = false; d.m.visible = false; if (Math.random() < 0.3) splashAt(D, p, t, 0.6); }
  }
  for (const s of D.splashes) {
    const k = (t - s.t0) / 0.45;
    if (k >= 1 || k < 0) { s.s.visible = false; continue; }
    s.s.visible = true; s.s.scale.setScalar((0.15 + k * 0.5) * sz * s.big); s.s.material.opacity = 0.75 * (1 - k);
  }
}
